import type { DeploymentAlertAPI } from '@analyzer/client';
import type { DeploymentClassification } from '@analyzer/core/lib/analyzer/plugins/changes/deployment.js';
import { Alert, AlertTitle, Box, Card, CardContent, Chip, Typography } from '@mui/material';
import SummarizedLink from '../url.js';
import { getSeverityColor } from './severity.js';

const classificationLabels: Record<DeploymentClassification, { title: string; description: string }> = {
  baseline: {
    title: 'Deployment Baseline Established',
    description: 'This is the first recorded deployment for this target.'
  },
  updated: {
    title: 'New Deployment Detected',
    description: 'A new deployment was detected. If this was unexpected, please investigate further.'
  },
  unchanged: {
    title: 'Deployment Unchanged',
    description: 'The deployment signature matches the previous snapshot.'
  },
  rollback: {
    title: 'Deployment Rollback Detected',
    description: 'The current deployment matches a previously seen version.'
  },
  replaced: {
    title: 'Deployment Fully Replaced',
    description: 'No overlap was found with the previous deployment bundle.'
  },
  injected: {
    title: 'Scripts Injected',
    description: 'New scripts were added to the deployment without content changes to existing files.'
  },
  stripped: {
    title: 'Scripts Removed',
    description: 'Scripts were removed from the deployment without content changes to existing files.'
  },
  restructured: {
    title: 'Deployment Restructured',
    description: 'Scripts were added and removed without content changes to existing files.'
  },
  unknown: {
    title: 'Deployment Change Detected',
    description: 'A deployment change was detected but could not be classified.'
  }
};

const fallback = {
  title: 'New Deployment Detected',
  description:
    'A new deployment of the target website was detected. If this was unexpected, please investigate further to ensure everything is working as intended.'
};

function RequestList({ requests }: { requests: { request_url: string }[] }) {
  return (
    <>
      {requests.map((item, index) => (
        <Typography key={index} variant="body2">
          <SummarizedLink url={item.request_url} />
        </Typography>
      ))}
    </>
  );
}

type ModifiedPair = NonNullable<DeploymentAlertAPI['changes']>['modified'][number];

function ModifiedList({ pairs }: { pairs: ModifiedPair[] }) {
  return (
    <>
      {pairs.map(({ before, after }, index) => {
        const urlBefore = before.request_url;
        const urlAfter = after.request_url;
        const urlChanged = urlBefore !== urlAfter;
        const contentChanged = before.response_sha256 !== after.response_sha256;
        const tag = urlChanged && contentChanged ? 'renamed + content' : urlChanged ? 'renamed' : 'content';
        return (
          <Typography key={index} variant="body2">
            {urlChanged ? (
              <>
                <SummarizedLink url={urlBefore} /> {' → '} <SummarizedLink url={urlAfter} />
              </>
            ) : (
              <SummarizedLink url={urlAfter} />
            )}{' '}
            <Typography component="span" variant="caption" color="text.secondary">
              ({tag})
            </Typography>
          </Typography>
        );
      })}
    </>
  );
}

export function DeploymentAlert(alert: DeploymentAlertAPI) {
  const { severity, classification, changes } = alert;
  const { title, description } = (classification && classificationLabels[classification]) ?? fallback;

  const hasChanges =
    changes && (changes.added.length > 0 || changes.removed.length > 0 || changes.modified.length > 0);

  return (
    <Box>
      <Alert severity={getSeverityColor(severity)} sx={{ mb: 2 }}>
        <AlertTitle>{title}</AlertTitle>
        <Typography variant="body2">{description}</Typography>
      </Alert>

      <Card>
        <CardContent>
          <Typography variant="h6">Details</Typography>
          <Typography variant="body2">Current Version: {alert.signature}</Typography>
          {classification && (
            <Box sx={{ mt: 1 }}>
              <Chip label={classification} size="small" variant="outlined" />
            </Box>
          )}

          {hasChanges ? (
            <>
              {changes.added.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Added ({changes.added.length})
                  </Typography>
                  <RequestList requests={changes.added} />
                </>
              )}
              {changes.removed.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Removed ({changes.removed.length})
                  </Typography>
                  <RequestList requests={changes.removed} />
                </>
              )}
              {changes.modified.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Modified ({changes.modified.length})
                  </Typography>
                  <ModifiedList pairs={changes.modified} />
                </>
              )}
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Bundle Scripts
              </Typography>
              <RequestList requests={alert.requests} />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
